import React, { useContext, useEffect, useState } from 'react';
import axios from "axios";
import moment from "moment";
import { createColumnHelper } from "@tanstack/react-table";
import { Badge, Button, Group, Modal, NumberInput, Stack, Text, Table, Code } from "@mantine/core";
import AuthContext from '../../../contexts/AuthContext';
import showNotification from '../../../utils/showNotification';
import { ERROR_HANDLER } from '../../../utils/error-handler';
import ContentCreator from '../../../components/ContentCreator/ContentCreator';
import TanStackTable from '../../../components/Table/TanStackTable/TanStackTable';
import RafflesForm from './RaffleForm';



const STATUS_LABELS = {
    DRAFT: { label: 'Borrador', color: 'gray' },
    ACTIVE: { label: 'Activo', color: 'green' },
    FINISHED: { label: 'Finalizado', color: 'blue' },
};

const EMPTY_RAFFLE = {
    slug: "",
    title: "",
    status: "DRAFT",
    isFeatured: false,
    startsAt: null,
    endsAt: null,
    content: {
        heading: "Tenemos",
        highlight: "sorteo",
        description: "",
        secondaryDescription: "",
        questionPrompt: "",
        prizeDescription: "",
        buttonLabel: "Participar",
        successTitle: "¡Ya estás participando del sorteo!",
        successMessageCorrect: "¡Felicidades! ¡Tenés doble chance de ganar!",
        successMessageWrong: "No respondiste la pregunta correctamente",
        coverImage: "",
    },
    form: {
        showSpecialties: true,
        showAlreadyStudent: true,
    },
    question: {
        enabled: true,
        chancesIfCorrect: 2,
    },
    lead: {
        source: "SORTEO_PAGE",
        flow: "SORTEO_FLOW",
        sendEmail: false,
    },
};

const createRaffle = async (values, config) => {
    const jsonData = { data: { ...values } };
    return axios.post(
        `${process.env.REACT_APP_BACKEND_SERVER}admin/marketing/raffles`,
        jsonData,
        config
    );
};

const editRaffle = async (values, config) => {
    const jsonData = {
        item: { _id: values._id },
        field: { ...values },
    };
    return axios.put(
        `${process.env.REACT_APP_BACKEND_SERVER}admin/marketing/raffles`,
        jsonData,
        config
    );
};

function RafflesContainer() {
    const { authToken } = useContext(AuthContext);

    const [rafflesData, setRafflesData] = useState(null);
    const [displayForm, setDisplayForm] = useState(false);
    const [initialValuesEdit, setInitialValuesEdit] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedRaffleId, setSelectedRaffleId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [updateData, setUpdateData] = useState(false);

    // Sorteo
    const [drawModalOpen, setDrawModalOpen] = useState(false);
    const [drawTarget, setDrawTarget] = useState(null);
    const [drawConfig, setDrawConfig] = useState({ winnersCount: 1, alternatesCount: 0 });
    const [drawPreview, setDrawPreview] = useState(null);
    const [drawLoading, setDrawLoading] = useState(false);

    const config = {
        headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + authToken,
        },
    };

    // COLUMN DEFINITIONS
    const columnHelper = createColumnHelper();
    const columns = [
        columnHelper.accessor('title', {
            cell: info => info.getValue(),
            header: 'Nombre',
            enableColumnFilter: true,
            enableSorting: false,
            filter: 'text',
            filterFn: 'includesString',
        }),
        columnHelper.accessor('slug', {
            cell: info => <Code>{info.getValue()}</Code>,
            header: 'Slug',
            enableColumnFilter: true,
            enableSorting: false,
            filter: 'text',
            filterFn: 'includesString',
        }),
        columnHelper.accessor('status', {
            cell: info => {
                const status = STATUS_LABELS[info.getValue()] || { label: info.getValue(), color: 'gray' };
                return <Badge color={status.color}>{status.label}</Badge>;
            },
            header: 'Estado',
            enableColumnFilter: true,
            enableSorting: false,
            filter: 'select',
            size: 64,
        }),
        columnHelper.accessor('entriesCount', {
            cell: info => info.getValue() ?? 0,
            header: 'Participantes',
            enableColumnFilter: false,
            enableSorting: true,
            size: 64,
        }),
        columnHelper.accessor('endsAt', {
            cell: info => {
                const value = info.getValue();
                return value ? moment(value).format("DD/MM/YYYY") : "—";
            },
            header: 'Cierre',
            enableColumnFilter: false,
            enableSorting: true,
            sortingFn: "datetime",
            size: 64,
        }),
        columnHelper.display({
            id: 'draw',
            header: 'Sorteo',
            size: 96,
            cell: ({ row }) => {
                const raffle = row.original;
                const alreadyDrawn = raffle.winners?.length > 0;

                return (
                    <Button
                        size="xs"
                        variant={alreadyDrawn ? "subtle" : "light"}
                        onClick={() => openDrawModal(raffle)}
                    >
                        {alreadyDrawn ? "Ver ganadores" : "Sortear"}
                    </Button>
                );
            },
        }),
    ];

    // TABLE FUNCTIONS
    const editRaffleRow = (rowData) => {
        if (!rowData) return;

        setInitialValuesEdit({
            ...EMPTY_RAFFLE,
            ...rowData,
            content: { ...EMPTY_RAFFLE.content, ...(rowData.content || {}) },
            form: { ...EMPTY_RAFFLE.form, ...(rowData.form || {}) },
            question: { ...EMPTY_RAFFLE.question, ...(rowData.question || {}) },
            lead: { ...EMPTY_RAFFLE.lead, ...(rowData.lead || {}) },
            startsAt: rowData.startsAt ? moment(rowData.startsAt).toDate() : null,
            endsAt: rowData.endsAt ? moment(rowData.endsAt).toDate() : null,
        });
        setDisplayForm(true);
    };

    const deleteRaffle = async (idRaffle = null) => {
        if (deleteModalOpen) {
            try {
                setLoading(true);
                await axios.delete(
                    `${process.env.REACT_APP_BACKEND_SERVER}admin/marketing/raffles`,
                    { ...config, data: { _id: selectedRaffleId } }
                );
                setUpdateData(!updateData);
                showNotification({
                    color: "green",
                    status: "success",
                    title: "¡Operación exitosa!",
                    message: "El sorteo seleccionado ha sido correctamente eliminado.",
                    autoClose: 12000,
                });
            } catch (error) {
                console.error(error);
                showNotification({
                    color: "red",
                    status: "error",
                    title: "Operación fallida.",
                    message: `No se pudo eliminar el sorteo. Detalle: ${
                        ERROR_HANDLER[error.request?.status] || ERROR_HANDLER.defaultError
                    }`,
                    autoClose: 12000,
                });
            } finally {
                setDeleteModalOpen(false);
                setLoading(false);
            }
        } else {
            setSelectedRaffleId(idRaffle);
            setDeleteModalOpen(true);
        }
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);

            if (initialValuesEdit) {
                await editRaffle(values, config);
            } else {
                await createRaffle(values, config);
            }

            showNotification({
                color: 'green',
                status: 'success',
                title: '¡Excelente! Operación exitosa.',
                message: `El sorteo ${values.title} ha sido creado / editado con éxito.`,
            });

            setUpdateData(!updateData);
        } catch (error) {
            showNotification({
                color: "red",
                status: "error",
                title: "Operación fallida.",
                message: `No se pudo crear / editar el sorteo. Detalle: ${
                    ERROR_HANDLER[error.request?.status] || ERROR_HANDLER.defaultError
                }`,
                autoClose: 12000,
            });
        } finally {
            setLoading(false);
            setDisplayForm(false);
            setInitialValuesEdit(null);
        }
    };

    // SORTEO
    const openDrawModal = (raffle) => {
        setDrawTarget(raffle);
        setDrawConfig({ winnersCount: 1, alternatesCount: 0 });
        setDrawPreview(null);
        setDrawModalOpen(true);
    };

    const runDraw = async (dryRun) => {
        if (!drawTarget) return;

        try {
            setDrawLoading(true);
            const { data } = await axios.post(
                `${process.env.REACT_APP_BACKEND_SERVER}admin/marketing/raffles/${drawTarget.slug}/draw`,
                { ...drawConfig, dryRun },
                config
            );

            setDrawPreview(data);

            if (!dryRun) {
                setUpdateData(!updateData);
                showNotification({
                    color: "green",
                    status: "success",
                    title: "Sorteo realizado",
                    message: `Semilla: ${data.seed}. Guardala como comprobante.`,
                    autoClose: false,
                });
            }
        } catch (error) {
            showNotification({
                color: "red",
                status: "error",
                title: "No se pudo sortear",
                message: error.response?.data?.message
                    || ERROR_HANDLER[error.request?.status]
                    || ERROR_HANDLER.defaultError,
                autoClose: 12000,
            });
        } finally {
            setDrawLoading(false);
        }
    };

    // DATA INITIALIZATION
    const init = async () => {
        try {
            const { data } = await axios.get(
                `${process.env.REACT_APP_BACKEND_SERVER}admin/marketing/raffles`,
                config
            );
            setRafflesData(data);
        } catch (error) {
            console.error(error);
            showNotification({
                color: "red",
                status: "error",
                title: "Error",
                message: "No se pudieron cargar los sorteos.",
            });
        }
    };

    useEffect(() => {
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateData]);

    const alreadyDrawn = drawTarget?.winners?.length > 0;

    return (
        <div className="rafflesAdmin__container">
            {displayForm && (
                <ContentCreator
                    title={initialValuesEdit ? "Editar sorteo" : "Crear sorteo"}
                    submitFunc={(values) => handleSubmit(values)}
                    onCancel={() => {
                        setDisplayForm(false);
                        setInitialValuesEdit(null);
                    }}
                    initialValues={initialValuesEdit ?? EMPTY_RAFFLE}
                    renderPreview={() => <></>}
                    renderForm={(_form) => (
                        <RafflesForm
                            form={_form.form}
                            loading={loading}
                            isEdit={Boolean(initialValuesEdit)}
                        />
                    )}
                />
            )}
            {rafflesData && (
                <TanStackTable
                    data={rafflesData}
                    columns={columns}
                    editFunc={editRaffleRow}
                    onDelete={deleteRaffle}
                    displayForm={displayForm}
                    addButtonFunc={() => {
                        setDisplayForm(!displayForm);
                        if (displayForm) setInitialValuesEdit(null);
                    }}
                />
            )}

            {/* <DeleteConfirmationModal
                opened={deleteModalOpen}
                loading={loading}
                onClose={() => setDeleteModalOpen(false)}
                onCancel={() => setDeleteModalOpen(false)}
                onConfirm={() => deleteRaffle(selectedRaffleId)}
            /> */}

            <Modal
                opened={drawModalOpen}
                onClose={() => setDrawModalOpen(false)}
                title={alreadyDrawn ? "Ganadores" : `Sortear: ${drawTarget?.title ?? ""}`}
                size="lg"
            >
                <Stack>
                    {!alreadyDrawn && (
                        <>
                            <Group grow>
                                <NumberInput
                                    label="Ganadores"
                                    min={1}
                                    value={drawConfig.winnersCount}
                                    onChange={(value) =>
                                        setDrawConfig({ ...drawConfig, winnersCount: value || 1 })
                                    }
                                />
                                <NumberInput
                                    label="Suplentes"
                                    min={0}
                                    value={drawConfig.alternatesCount}
                                    onChange={(value) =>
                                        setDrawConfig({ ...drawConfig, alternatesCount: value || 0 })
                                    }
                                />
                            </Group>

                            <Text size="xs" c="dimmed">
                                Simulá primero para ver el resultado sin guardarlo. Al confirmar,
                                el sorteo pasa a FINALIZADO y deja de aceptar participantes.
                            </Text>
                        </>
                    )}

                    {drawPreview && (
                        <Stack gap={8}>
                            <Text size="sm">
                                {drawPreview.totalEntries} participantes · {drawPreview.totalChances} chances
                                {drawPreview.dryRun && " · SIMULACIÓN"}
                            </Text>

                            <Table striped withTableBorder>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>#</Table.Th>
                                        <Table.Th>Nombre</Table.Th>
                                        <Table.Th>Email</Table.Th>
                                        <Table.Th>Chances</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {drawPreview.winners.map((w) => (
                                        <Table.Tr key={w._id}>
                                            <Table.Td>{w.position}</Table.Td>
                                            <Table.Td>{w.name || "—"}</Table.Td>
                                            <Table.Td>{w.email}</Table.Td>
                                            <Table.Td>{w.chances}</Table.Td>
                                        </Table.Tr>
                                    ))}
                                    {drawPreview.alternates?.map((w) => (
                                        <Table.Tr key={w._id}>
                                            <Table.Td>S{w.position}</Table.Td>
                                            <Table.Td>{w.name || "—"}</Table.Td>
                                            <Table.Td>{w.email}</Table.Td>
                                            <Table.Td>{w.chances}</Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>

                            <Text size="xs" c="dimmed">
                                Semilla: <Code>{drawPreview.seed}</Code>
                            </Text>
                        </Stack>
                    )}

                    {!alreadyDrawn && (
                        <Group justify="flex-end">
                            <Button
                                variant="default"
                                loading={drawLoading}
                                onClick={() => runDraw(true)}
                            >
                                Simular
                            </Button>
                            <Button
                                color="red"
                                loading={drawLoading}
                                disabled={!drawPreview}
                                onClick={() => runDraw(false)}
                            >
                                Confirmar sorteo
                            </Button>
                        </Group>
                    )}
                </Stack>
            </Modal>
        </div>
    );
}

export default RafflesContainer;